import { usePreviousPage, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  CopyButton,
  Link,
  LinkButton,
  Owners,
  PublishedProductText,
  TimeIndicator
} from 'components/common';
import { Icon } from 'components/common/icons';
import { CourseHeaderShare, CoursePicture } from '.';

import type { PageState } from 'types';

interface Props {
  course: PageState['course'];
}

const CourseHeader = ({ course }: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();
  const { as, href, title } = usePreviousPage({ fallbackUrl: '/courses' });

  return (
    <div className="header">
      <div className="container">
        <div className="content">
          <div className="title-wrapper">
            <Link aria-label={title} as={as} href={href}>
              <div className="back-link">
                <Icon icon="arrow-left" />
              </div>
            </Link>

            <div className="picture">
              <CoursePicture
                position={course.picturePosition}
                size={480}
                url={course.picture}
              />
            </div>

            <div className="title">
              <div className="title-text">{course.title}</div>
              <div className="info">
                <div className="owners">
                  <Owners owners={course.owners} />
                </div>
                <div
                  className="edited"
                  data-tooltip={messages.courseLastUpdated}
                >
                  <TimeIndicator
                    language={language}
                    timeString={course.updated}
                  />
                </div>
                <div className="published">
                  <PublishedProductText
                    // Remount when the dates change, so the status is read
                    // against a fresh time after publishing or ending
                    key={`${course.startDate}-${course.endDate}`}
                    endDate={course.endDate as string}
                    startDate={course.startDate as string}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="links">
            <LinkButton
              category="light"
              href={`/courses/${course.slug}`}
              size="small"
              style={{ marginRight: 20 }}
              text={messages.commonShowUserPage}
            />

            <CopyButton
              buttonStyle={{ width: 28, height: 28, padding: '6px 0 7px' }}
              category="light"
              data-tooltip={messages.commonCopyLink}
              icon="link"
              size="small"
              style={{ marginRight: 10 }}
              successText={messages.commonCopied}
              value={`${serverConfig.adminUrl}/courses/${course.slug}`}
            />

            {serverConfig.courseEnableShareCode ||
            serverConfig.courseEnableShareQRCode ? (
              <CourseHeaderShare
                style={{ marginRight: 10, width: 28, height: 28 }}
              />
            ) : null}

            <LinkButton
              href={
                `/courses/${course.slug}` +
                (course.chapters.length ? `/${course.chapters[0].slug}` : '')
              }
              icon="book"
              size="small"
              text={messages.coursesRead}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .header {
          border-bottom: 1px solid ${theme.lightGreyDark};
          margin-bottom: 20px;
        }
        .content {
          align-items: top;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          flex-shrink: 1;
          gap: 15px 0;
          padding: 18px 0;
        }
        .title-wrapper {
          display: flex;
          flex-grow: 1;
          flex-shrink: 1;
        }
        .back-link {
          align-items: center;
          background-color: ${theme.pageIconBackgroundColor || 'white'};
          border-radius: 50%;
          color: ${theme.pageIconTextColorLighter || theme.textColorLighter};
          display: inline-flex;
          font-size: 1.2rem;
          justify-content: center;
          margin-right: 15px;
          margin-top: 5px;
          text-decoration: none;
          transition: background-color 0.3s;
          height: 28px;
          width: 28px;
        }
        .back-link:focus,
        .back-link:hover {
          color: ${theme.pageIconTextColor || theme.textColor};
        }
        .picture {
          border-radius: 6px;
          flex-grow: 0;
          flex-shrink: 0;
          margin-top: 3px;
          overflow: hidden;
          position: relative;
          width: 32px;
          height: 32px;
        }
        .title {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          flex-shrink: 1;
          line-height: 1.1;
          margin-left: 10px;
        }
        .title-text {
          color: ${theme.pageTextColor || theme.textColor};
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.2;
          margin: 0;
        }
        .info {
          display: inline-flex;
          flex-direction: column;
          color: ${theme.pageTextColorLighter || theme.textColorLighter};
          font-size: 1.3rem;
          line-height: 1.5;
        }
        .owners {
          font-weight: 600;
          margin-right: 10px;
        }
        .edited {
          margin-right: 10px;
        }
        .published {
          font-weight: 600;
        }
        .links {
          flex-grow: 0;
          flex-shrink: 0;
          margin-top: 3px;
          white-space: nowrap;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .content {
            flex-direction: row;
          }
          .info {
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseHeader;
